import { createRouter, createWebHistory } from "@ionic/vue-router";
import { RouteRecordRaw } from "vue-router";
import { usePostHog } from "@/composables/usePosthog";
import { useMyUser } from "seedgreen-shared/queries/user";
import { sendToast } from "@/utils/sendToast";
import { until } from "@vueuse/core";

const routes: RouteRecordRaw[] = [
	{
		path: "",
		redirect: "/lot-search",
	},
	{
		path: "/lot-search",
		name: "lot-search",
		component: () => import("@/views/LotSearch/LotSearchPage.vue"),
	},
	{
		path: "/tasks",
		component: () => import("@/views/Tasks/TasksPage.vue"),
	},
	// {
	// 	path: "/notes",
	// 	component: () => import("@/views/Notes/NotesPage.vue"),
	// },
	{
		path: "/chemical/:taskId/edit",
		name: "chemical.edit",
		component: () => import("@/views/Tasks/ChemicalTaskEdit.vue"),
	},
	{
		path: "/harvest/berries",
		component: () => import("@/views/Harvest/Berries/BerriesPage.vue"),
		children: [
			{
				path: "overview",
				component: () =>
					import(
						"@/views/Harvest/Berries/Overview/BerriesOverview.vue"
					),
			},
			{
				path: "reports",
				component: () =>
					import(
						"@/views/Harvest/Berries/Reports/BerriesReports.vue"
					),
			},
			{
				path: "analytics",
				component: () =>
					import(
						"@/views/Harvest/Berries/Analytics/BerriesAnalytics.vue"
					),
			},
		],
	},
	{
		path: "/harvest/seasons",
		name: "seasons",
		component: () => import("@/views/Harvest/Seasons/SeasonsPage.vue"),
	},
	{
		path: "/harvest/seasons/:id",
		name: "season-details",
		component: () =>
			import("@/views/Harvest/Seasons/SeasonDetailsPage.vue"),
		props: (route) => ({ seasonId: Number(route.params.id) }),
	},

	{
		path: "/reports",
		name: "reports",
		component: () => import("@/views/Reports/ReportsPage.vue"),
	},
	{
		path: "/settings/users",
		component: () =>
			import("@/views/Settings/UserManagement/UserManagementPage.vue"),
	},
	{
		path: "/settings/roles",
		component: () =>
			import("@/views/Settings/UserManagement/RoleManagementPage.vue"),
	},
	{
		path: "/settings/customer",
		component: () =>
			import(
				"@/views/Settings/CustomerManagement/CustomerManagementPage.vue"
			),
	},
	{
		path: "/settings/observation-categories",
		component: () =>
			import(
				"@/views/Settings/ObservationCategories/ObservationCategoriesPage.vue"
			),
	},
	{
		path: "/internal",
		name: "internal",
		component: () =>
			import("@/views/InternalDashboard/InternalDashboardPage.vue"),
		meta: {
			requiresInternal: true,
		},
	},
	{
		path: "/internal/users",
		name: "internal-users",
		component: () =>
			import("@/views/InternalDashboard/InternalUserManagementPage.vue"),
		meta: {
			requiresInternal: true,
		},
	},
	{
		path: "/internal/customers",
		name: "internal-customers",
		component: () =>
			import(
				"@/views/InternalDashboard/InternalCustomerManagementPage.vue"
			),
		meta: {
			requiresInternal: true,
		},
	},
	{
		path: "/calendar",
		name: "calendar",
		component: () => import("@/views/Calendar/CalendarPage.vue"),
	},

	// Anonymous routes (no authentication required)
	{
		path: "/invite/:token",
		name: "invite-signup",
		component: () => import("@/views/Auth/InviteSignupPage.vue"),
		meta: {
			anonymous: true,
		},
	},
	{
		path: "/password-reset",
		name: "password-reset",
		component: () => import("@/views/Auth/PasswordResetPage.vue"),
		meta: {
			anonymous: true,
		},
	},
];

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes,
});

router.afterEach(() => {
	const { posthog } = usePostHog();
	posthog.capture("$pageview");
});

router.beforeEach(async (to, from) => {
	const { posthog } = usePostHog();
	if (from.path !== to.path) {
		posthog.capture("$pageleave");
	}

	// Check if route requires internal user access
	if (to.meta?.requiresInternal) {
		const { myUser, isPending } = useMyUser();

		if (isPending.value) {
			sendToast("Additional authentication required, pending...", 3000);

			try {
				await until(isPending).toBe(false, {
					timeout: 2000,
					throwOnTimeout: true,
				});
			} catch {
				sendToast("Authentication check timed out...", 3000, "danger");
				return { path: "/" };
			}
		}

		if (myUser.value?.internalUser) {
			return;
		}

		// User is not internal or auth filed, redirect to home
		return { path: "/" };
	}
});

export default router;
